import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { WatchlistService } from '../../../../core/services/watchlist.service';
import { LoggingService } from '../../../../core/services/logging.service';
import { Watchlist } from '../../../../shared/models/watchlist.model';
import { WatchlistCreateDialogComponent } from '../create-dialog/watchlist-create-dialog.component';

@Component({
  selector: 'app-watchlist-add-ticker-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    SelectModule,
    WatchlistCreateDialogComponent
  ],
  templateUrl: './watchlist-add-ticker-dialog.component.html',
  styleUrl: './watchlist-add-ticker-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchlistAddTickerDialogComponent {
  private watchlistService = inject(WatchlistService);
  private messageService = inject(MessageService);
  private logger = inject(LoggingService);

  @ViewChild(WatchlistCreateDialogComponent) createDialog?: WatchlistCreateDialogComponent;
  @Output() tickerAdded = new EventEmitter<void>();

  visible = signal(false);
  isLoading = signal(false);
  watchlists = signal<Watchlist[]>([]);
  selectedWatchlist = signal<Watchlist | null>(null);
  ticker = signal<string>('');

  constructor() {
    // Reset form when dialog closes
    effect(() => {
      if (!this.visible()) {
        this.resetForm();
      }
    });
  }

  /**
   * Show the dialog for a specific ticker
   */
  show(ticker: string): void {
    this.ticker.set(ticker);
    this.visible.set(true);
    this.loadWatchlists();
    this.resetForm();
  }

  /**
   * Hide the dialog
   */
  hide(): void {
    this.visible.set(false);
  }

  /**
   * Reset form fields
   */
  private resetForm(): void {
    this.selectedWatchlist.set(null);
  }

  /**
   * Load all watchlists
   */
  private loadWatchlists(): void {
    this.isLoading.set(true);

    this.watchlistService.getAllWatchlists().subscribe({
      next: (watchlists) => {
        this.watchlists.set(watchlists);
        this.isLoading.set(false);

        // Auto-select if there's only one watchlist
        if (watchlists.length === 1) {
          this.selectedWatchlist.set(watchlists[0]);
        }
      },
      error: (error) => {
        this.logger.error('Error loading watchlists:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load watchlists'
        });
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Check if form is valid
   */
  isFormValid(): boolean {
    return this.watchlists().length > 0 && this.selectedWatchlist() !== null;
  }

  /**
   * Handle watchlist selection change
   */
  onWatchlistChange(event: { value: Watchlist | null }): void {
    this.selectedWatchlist.set(event.value);
  }

  /**
   * Open create watchlist dialog
   */
  openCreateDialog(): void {
    this.createDialog?.show();
  }

  /**
   * Handle watchlist created
   */
  onWatchlistCreated(watchlist: Watchlist): void {
    // Reload watchlists to include the new one
    this.loadWatchlists();
    // Select the newly created watchlist
    this.selectedWatchlist.set(watchlist);
    this.messageService.add({
      severity: 'success',
      summary: 'Created',
      detail: `Watchlist "${watchlist.name}" created successfully`
    });
  }

  /**
   * Add ticker to the selected watchlist
   */
  addTicker(): void {
    const watchlist = this.selectedWatchlist();
    const ticker = this.ticker();

    if (!watchlist || !ticker) {
      return;
    }

    this.isLoading.set(true);

    this.watchlistService.addTicker(watchlist.id, ticker).subscribe({
      next: (result) => {
        if (result !== null) {
          this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: `${ticker} added to ${watchlist.name}`
          });
          this.tickerAdded.emit();
          this.hide();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add ticker to watchlist'
          });
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        this.logger.error('Error adding ticker to watchlist:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add ticker to watchlist'
        });
        this.isLoading.set(false);
      }
    });
  }
}
